/**
 * The flat namespace FiveM streams into, and what goes wrong inside it.
 *
 * A resource can put its models wherever it likes. FiveM does not care. Every
 * file under a folder called `stream` is handed to the game by *name*, and the
 * game's streaming module has no concept of a directory: `police.yft` is
 * `police.yft` whether it arrived from `stream/police.yft`, from
 * `stream/vehicles/lspd/2020/police.yft`, or from a different resource
 * altogether. Two resources that both ship that name are shipping one asset, and
 * exactly one of them wins.
 *
 * That is the whole reason this module exists, and it is why the naive
 * implementation — the one every folder-scanning tool starts with — is wrong.
 * The naive version compares paths. Paths are unique almost by construction, so
 * it finds nothing, reports a clean pack, and the user discovers on their live
 * server that one of their two police cars is invisible with nothing in the
 * console to say why. Comparing lower-cased `name.extension` and nothing else is
 * what makes the conflict visible before it ships.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DISTINCTION THIS MODULE IS BUILT AROUND
 *
 * Not every shared name is a fault, and getting that wrong is how a tool loses a
 * modder for good. Replacing a stock asset is the single most common thing
 * anyone does in this hobby: you ship `police.yft` precisely *because* the base
 * game owns `police.yft`, and the game loading yours instead of Rockstar's is
 * the feature. Telling that author they have a "conflict" teaches them that this
 * tool does not know what it is looking at, and they stop reading the rest of
 * the report.
 *
 * So the three cases are kept apart, deliberately:
 *
 *   two resources, one name        → `stream.basename.duplicate`, error.
 *                                    Silent, load-order-dependent, a real bug.
 *   one resource, name is stock    → `stream.basename.vanilla_override`, warning.
 *                                    Almost certainly intentional. Worth saying
 *                                    once, because a replacement is global.
 *   one resource, two paths        → `stream.path.duplicate`, warning.
 *                                    The author's own copy of their own file.
 *
 * And when no vanilla name index was supplied — or when one was supplied and the
 * lazy load behind it failed — the override check does not quietly decide the
 * answer is "not stock". It goes in `skipped`, because a report that drops half
 * its rules in silence is worse than one that admits it.
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * SCOPE — what this deliberately does not do
 *
 * It does not read a single byte of a model. Everything here is a decision about
 * names, which is exactly as far as names can take you: two files called
 * `police.yft` collide whether they are the same model or not, and this module
 * cannot and does not say which one you want to keep.
 *
 * It also does not detect orphans (`stream.asset.orphan`). An orphan is an asset
 * nothing references, and nothing here has read the `.meta` files that do the
 * referencing; that check belongs to the resource analyzer, which has them.
 *
 * `spawnCandidates` is a ranking, not an answer. The authoritative list of spawn
 * names lives in `vehicles.meta`, and a caller that has one should use it and
 * ignore this. This is the fallback for the very common case of a pack that
 * ships models and no metadata, and it says out loud how confident it is and
 * which file each guess came from, because a guess presented as a fact is the
 * failure mode the prior art actually shipped. For the same reason every
 * sentence written here names a file that is really in the pack: telling someone
 * their model "ships alongside police.ytd" when the pack only holds
 * `police+hi.ytd` sends them hunting for a file that does not exist.
 *
 * Every walk over caller-supplied data is capped and every cap says which view
 * it protects. A whole server `resources` tree is a plausible drop here, and the
 * tables these results feed render one row per result.
 */

import { STREAM_EXTENSIONS as SHARED_STREAM_EXTENSIONS } from "./data/dataFileKinds.v1";
import { disambiguateLabels, PROSE_LABEL_STYLE } from "./labels";
import {
  baseName,
  extensionOf,
  invalidSpawnNameChars,
  isHighLodName,
  isInStreamFolder,
  isValidSpawnName,
  normalizeName,
  normalizePath,
  stemOf,
  stripHighLod,
} from "./normalize";
import type { GtaFile, GtaFinding, StreamAsset, VanillaAssetNameIndex } from "./types";

// ─── Limits ──────────────────────────────────────────────────────────────────

/**
 * Cap the resources folded into one namespace. PackBench's collision view lists
 * a row per resource that ships a contested name, and someone dropping their
 * whole server folder in is the normal way this module gets used.
 */
const MAX_RESOURCES_SCANNED = 2000;

/**
 * Cap the files read out of one resource. A dropped zip can contain a whole
 * `dlcpacks` tree, and PackBench's file table renders every row it is given.
 */
const MAX_FILES_PER_RESOURCE = 50000;

/**
 * Cap the names walked by every analysis pass, so the desktop report's findings
 * list and the Spawn List view both stay finite on a pathological folder.
 */
const MAX_ASSETS_SCANNED = 50000;

/**
 * How many resources a collision message names before it summarises. A finding's
 * detail is one paragraph in a card; twelve resource names is not a paragraph.
 */
const MAX_RESOURCES_NAMED = 6;

/** The same limit for the path list in a duplicate-copy finding, in the same card. */
const MAX_PATHS_NAMED = 8;

/** Cap the spawn list, which the Spawn List view renders as one copyable block. */
const MAX_SPAWN_CANDIDATES = 5000;

/** Cap the companion grouping, which feeds that same list. */
const MAX_HIGH_LOD_GROUPS = 5000;

// ─── What the stream namespace contains ──────────────────────────────────────

/**
 * The extensions the streaming module actually consumes.
 *
 * Anything else in a `stream` folder — a readme, a `.png` reference sheet, an
 * `.ini` left over from a conversion — is inert cargo, and reporting a collision
 * between two resources' `readme.txt` would be noise about a file the game never
 * looks at.
 *
 * The set is built from the shared table rather than written out again. It was
 * a hand-kept copy of the same thirteen entries until Phase 3, which is one fact
 * in two places and therefore one place that can be quietly wrong: the shared
 * table is what `analyze.ts`, Code Smith and the desktop scanner read, and a
 * `collectStreamAssets` that disagreed with it would report a collision on an
 * extension the missing-declaration check had already decided was not streamed.
 * `SHARED_STREAM_EXTENSIONS` is a `readonly string[]` and this module wants
 * membership, so the `Set` is the only thing built here.
 */
const STREAM_EXTENSIONS: ReadonlySet<string> = new Set(SHARED_STREAM_EXTENSIONS);

/**
 * The extensions that exist for no reason other than to be streamed.
 *
 * These are what makes it safe to look outside a `stream` folder at all. Plenty
 * of packs arrive flattened — a zip that lost its structure, a folder someone
 * assembled by hand — and refusing to look at a `.yft` because it is not under
 * `stream/` means reporting nothing at all about a pack that is entirely models.
 *
 * `.ymt`, `.ymap`, `.ytyp` and `.ynd` are excluded from that fallback on
 * purpose: those are the four a resource routinely ships in `data/` as declared
 * `data_file` entries, and treating a correctly-placed `data/vehiclelayouts.ymt`
 * as a streamed asset would manufacture an error-severity collision out of two
 * resources that are both doing the right thing.
 */
const ALWAYS_STREAMED_EXTENSIONS: ReadonlySet<string> = new Set([
  "yft",
  "ytd",
  "ydr",
  "ydd",
  "ybn",
  "ycd",
  "awc",
  "ynv",
  "ypt",
]);

/**
 * Ordering a saved report can be diffed against.
 *
 * `localeCompare` is the obvious choice and the wrong one: its answer depends on
 * the ICU data of the machine it runs on, so the same pack scanned in the
 * browser and in the desktop app can produce two different orderings of the same
 * findings, and a CI baseline that nobody changed starts showing a diff.
 */
function compareText(a: string, b: string): number {
  if (a === b) return 0;
  return a < b ? -1 : 1;
}

// ─── Collection ──────────────────────────────────────────────────────────────

/** One occurrence, named so the helpers below can talk about it. */
type StreamOccurrence = StreamAsset["occurrences"][number];

/**
 * Fold every resource's files into one entry per name in the stream namespace.
 *
 * The key is `baseName(path).toLowerCase()` rather than the collector's own
 * `name` field, because collectors disagree about whether `name` carries the
 * extension and a path never does. Deriving it from the path means a zip reader
 * and a directory walker produce the same key for the same file, which is the
 * whole point of having one shared implementation.
 *
 * Nothing here throws. A malformed entry — no path, no extension, an empty name
 * — is skipped, because the caller handed us a file list and the useful response
 * to one bad row is the other nine hundred.
 *
 * `identity` and `origin` are how a caller says which of two identically-named
 * resources this one is. They are optional and fall back to the name, which is
 * correct for a single resource and wrong for two: keying the per-path dedupe on
 * a name that two vendor packs share is what made the second pack's
 * `stream/police.yft` look like a re-listing of the first's and drop it, leaving
 * one occurrence where there were two and no collision left to report.
 *
 * `options.skipped` is where this says what it did not read. The resource cap
 * below used to `break` in silence, which is the failure this layer is written
 * against wearing a different hat: a 2,500-resource server folder is well inside
 * intake's ceiling and is the gesture the cap's own comment calls normal, and
 * resources 2,001 onward were never folded into the namespace — so no collision
 * involving them was reported, every one of them still had its own section in
 * the document, and the footer said nothing was skipped. The sink is optional so
 * the older call shape still compiles, but `analyze.ts` passes one.
 */
export function collectStreamAssets(
  resources: {
    name: string;
    /**
     * What tells this resource from the one beside it — `GtaResourceInput.identity`.
     *
     * Unique within one drop, which is as far as `intake.ts` can make it unique.
     * A caller that unions two drops can hand two packs the same one, and the
     * dedupe below then treats the second pack's files as re-listings of the
     * first's; `collectResources` reports that collapse in words rather than
     * letting it be silent. See `CollectableGroup.root`.
     */
    identity?: string;
    /** The archive or pick it came from, for the sentence a finding prints. */
    origin?: string;
    files: GtaFile[];
  }[],
  options?: { skipped?: string[] },
): StreamAsset[] {
  const byBasename = new Map<string, StreamAsset>();
  /** Paths already recorded, so a collector that lists a file twice counts it once. */
  const seenPaths = new Set<string>();

  const total = resources?.length ?? 0;
  let resourcesRead = 0;
  for (const resource of resources ?? []) {
    resourcesRead += 1;
    if (resourcesRead > MAX_RESOURCES_SCANNED) {
      options?.skipped?.push(
        `Stream namespace: only the first ${MAX_RESOURCES_SCANNED.toLocaleString("en-US")} of ` +
          `${total.toLocaleString("en-US")} resources were folded into it, so no collision ` +
          "involving the rest was looked for.",
      );
      break;
    }
    if (!resource || !Array.isArray(resource.files)) continue;
    const resourceName = typeof resource.name === "string" ? resource.name : "";
    const declaredIdentity = typeof resource.identity === "string" ? resource.identity : "";
    const resourceIdentity = declaredIdentity !== "" ? declaredIdentity : resourceName;
    const resourceOrigin = typeof resource.origin === "string" ? resource.origin : "";

    // Sliced once, so the `stream` folder probe below is bounded by the same cap
    // as the walk itself rather than reading a million rows before the cap is
    // ever consulted.
    const files = resource.files.slice(0, MAX_FILES_PER_RESOURCE);
    const hasStreamFolder = files.some(
      (file) => file && typeof file.path === "string" && isInStreamFolder(file.path),
    );

    for (const file of files) {
      if (!file || typeof file.path !== "string") continue;

      const path = normalizePath(file.path);
      const declared = normalizeName(typeof file.extension === "string" ? file.extension : "");
      const extension = extensionOf(path) || declared;
      if (extension === "" || !STREAM_EXTENSIONS.has(extension)) continue;

      // Outside a `stream` folder we are guessing, so we only guess about the
      // extensions that have no other job, and only when the resource has no
      // `stream` folder we could have looked in instead.
      if (!isInStreamFolder(path)) {
        if (hasStreamFolder) continue;
        if (!ALWAYS_STREAMED_EXTENSIONS.has(extension)) continue;
      }

      const basename = streamBasename(path, extension);
      if (basename === "") continue;

      // Keyed on the resource's *identity*, not its name. The job of this set is
      // to count a file once when a collector lists it twice, and a name is not
      // the resource: two vendor zips both shipping `police/stream/police.yft`
      // produce the same name and the same relative path, so keying on the name
      // discards the second archive's copy as a re-listing of the first's — and
      // with it the one collision the whole scan was run to find.
      //
      // Joined on NUL rather than a space, because an identity and a path can
      // each contain one: `"lspd pack" + "a.yft"` must not become the same key
      // as `"lspd" + "pack a.yft"` and silently swallow a real occurrence.
      const dedupeKey = `${resourceIdentity.toLowerCase()}\u0000${path}`;
      if (seenPaths.has(dedupeKey)) continue;
      seenPaths.add(dedupeKey);

      const occurrence: StreamOccurrence = {
        resource: resourceName,
        resourceIdentity,
        ...(resourceOrigin !== "" ? { resourceOrigin } : {}),
        path,
        size: sizeOf(file.size),
      };
      const existing = byBasename.get(basename);
      if (existing) {
        existing.occurrences.push(occurrence);
        continue;
      }
      byBasename.set(basename, {
        basename,
        extension,
        occurrences: [occurrence],
        vanilla: false,
        ...baseAssetOf(basename),
      });
    }
  }

  const assets = [...byBasename.values()];
  for (const asset of assets) {
    // Identity breaks the tie the name no longer can. Two occurrences from two
    // packs both called `police` at the same relative path are otherwise equal
    // on every field this compared, and a saved report would reorder them
    // between runs for no reason a reader could see.
    asset.occurrences.sort(
      (a, b) =>
        compareText(a.resource, b.resource) ||
        compareText(a.resourceIdentity ?? a.resource, b.resourceIdentity ?? b.resource) ||
        compareText(a.path, b.path),
    );
  }
  assets.sort((a, b) => compareText(a.basename, b.basename));
  return assets;
}

/**
 * A file size the report can add up.
 *
 * Collectors have handed us strings before, and `"4096" + "4096"` is `"40964096"`
 * bytes, which is how a duplicate-copy warning ends up claiming a resource
 * wastes 39 terabytes. Anything that is not a real positive number counts as
 * nothing, which under-reports the waste rather than inventing it.
 */
function sizeOf(size: unknown): number {
  return typeof size === "number" && Number.isFinite(size) && size > 0 ? size : 0;
}

/** `stream/Police.YFT` → `police.yft`, with the extension restored if the path lost it. */
function streamBasename(path: string, extension: string): string {
  const name = baseName(path).toLowerCase();
  if (name === "") return "";
  const withExtension = extensionOf(name) === "" ? `${name}.${extension}` : name;
  // `stream/.yft` is an extension with nothing in front of it. The streaming
  // module keys on the name, so there is no name here for the game to ask for
  // and nothing worth reporting a collision about.
  return withExtension.startsWith(".") ? "" : withExtension;
}

/**
 * The base asset a high-detail companion belongs to, when there is one.
 *
 * Models use `_hi` and texture dictionaries use `+hi`, which is not a typo in
 * Rockstar's files or in ours — see `normalize.ts`. Returned as an object to
 * spread so the optional property is simply absent on an ordinary asset rather
 * than present and undefined.
 */
function baseAssetOf(basename: string): { highLodOf?: string } {
  if (!isHighLodName(basename)) return {};
  const base = stripHighLod(basename);
  if (base === basename || base.startsWith(".")) return {};
  return { highLodOf: base };
}

// ─── Analysis ────────────────────────────────────────────────────────────────

const NO_VANILLA_INDEX_SKIP =
  "Vanilla override check: no base-game asset name index was supplied, so this report cannot " +
  "tell a deliberate replacement of a stock asset from an ordinary custom name.";

const BROKEN_VANILLA_INDEX_SKIP =
  "Vanilla override check: a base-game asset name index was supplied but could not be read, so " +
  "this report cannot tell a deliberate replacement of a stock asset from an ordinary custom " +
  "name. Collisions between resources were still checked, because they do not depend on it.";

/**
 * Turn the folded namespace into findings, keeping the three cases apart.
 *
 * The order of the checks is the point. A name shipped by two resources is
 * reported as a collision *whether or not* the base game owns it, and if it does
 * that fact goes into the sentence rather than becoming a second finding: the
 * author's problem is that two of their resources are fighting, and the stock
 * asset is context for that rather than a separate thing to fix.
 */
export function analyzeStreamAssets(
  assets: readonly StreamAsset[],
  options?: {
    vanillaNames?: VanillaAssetNameIndex;
    /**
     * Labels decided by a caller that can see more resources than these assets.
     *
     * Keyed by lower-cased identity. A set analysis labels every resource in the
     * set once and hands the same map to the set pass and to each resource's own
     * pass, so a per-resource finding cannot spell a pack differently from the
     * cross-resource finding above it. Absent, the labels are worked out from
     * the assets in hand, which is right for a caller that has them all.
     */
    labels?: ReadonlyMap<string, string>;
  },
): { findings: GtaFinding[]; skipped: string[] } {
  const findings: GtaFinding[] = [];
  const skipped: string[] = [];
  const vanillaNames = options?.vanillaNames;
  const hasIndex = !!vanillaNames && typeof vanillaNames.has === "function";
  if (!hasIndex) skipped.push(NO_VANILLA_INDEX_SKIP);
  let indexFailed = false;

  // Computed over every resource in the whole namespace before any sentence is
  // written, rather than per finding over the handful of resources one asset
  // touches. Ambiguity is a property of the report: `stream.path.duplicate` and
  // `stream.basename.vanilla_override` each fire inside a single resource, saw a
  // one-entry list, and therefore never widened — so "police ships extra.yft at
  // 2 paths" was equally true of the other police in the same document and named
  // neither. One index also means every finding in the report spells a pack the
  // same way, which a per-finding widener cannot promise.
  const labelOf = options?.labels ?? resourceLabelIndex(assets ?? []);

  let analyzed = 0;
  let overflowed = false;
  for (const asset of assets ?? []) {
    analyzed += 1;
    if (analyzed > MAX_ASSETS_SCANNED) {
      overflowed = true;
      break;
    }
    if (!asset || typeof asset.basename !== "string" || asset.basename === "") continue;

    const occurrences = (asset.occurrences ?? []).filter(isUsableOccurrence);
    if (occurrences.length === 0) continue;
    const resources = distinctResources(occurrences);

    const stock = hasIndex ? lookUpVanillaName(asset.basename, vanillaNames) : false;
    if (stock === null) indexFailed = true;
    const vanilla = asset.vanilla === true || stock === true;

    const labels = resources.map(
      (resource) => labelOf.get(normalizeName(resource.identity)) ?? resource.name,
    );

    if (resources.length > 1) {
      findings.push(collisionFinding(asset, occurrences, labels, vanilla));
      continue;
    }

    // Inside one resource the two problems are independent: a second copy of the
    // file is a housekeeping mistake, and the name being Rockstar's is a
    // statement about the whole world. Either can be true without the other.
    const only = labels[0] ?? "";
    if (occurrences.length > 1) findings.push(pathDuplicateFinding(asset, occurrences, only));
    if (vanilla) findings.push(vanillaOverrideFinding(asset, occurrences, only));
  }

  if (indexFailed) skipped.push(BROKEN_VANILLA_INDEX_SKIP);
  if (overflowed) {
    // `skipped` exists for exactly this and was not being used for it. A pass
    // that stops at 50,000 names and says nothing produces a document in which
    // every resource still has its own section, so it looks complete, while no
    // collision involving name 50,001 onward is reported and the footer reads
    // "Checks that did not run: none recorded" — a green all-clear produced by a
    // cap rather than by a clean namespace.
    skipped.push(
      `Only the first ${MAX_ASSETS_SCANNED.toLocaleString("en-US")} of ` +
        `${(assets?.length ?? 0).toLocaleString("en-US")} streamed names were checked, so any ` +
        "collision, duplicate path or base-game override among the rest was not looked for.",
    );
  }

  findings.sort(
    (a, b) =>
      severityRank(a.severity) - severityRank(b.severity) ||
      compareText(a.code, b.code) ||
      compareText(a.entity?.id ?? "", b.entity?.id ?? ""),
  );
  return { findings, skipped };
}

/**
 * An occurrence a finding can point at.
 *
 * Every message here names the file it is about, so an occurrence with no path
 * is not a lesser occurrence, it is one there is nothing to say about — and
 * reaching into it for a `file` field is how a report crashes instead of
 * printing the other two hundred findings it had already collected.
 */
function isUsableOccurrence(occurrence: StreamOccurrence): boolean {
  return !!occurrence && typeof occurrence.path === "string" && occurrence.path !== "";
}

function severityRank(severity: GtaFinding["severity"]): number {
  if (severity === "error") return 0;
  return severity === "warning" ? 1 : 2;
}

/** One resource that ships a name: the key it is counted by, and the words for it. */
type ResourceRef = { identity: string; name: string; origin: string };

/**
 * The resources that actually ship this name, case-folded once, in a stable order.
 *
 * Counted by identity rather than by name, because the count is the collision
 * test — `resources.length > 1` is what fires `stream.basename.duplicate`. Two
 * vendor packs both called `police` fold to one entry under a name key, the
 * guard reads false, and the tool's headline finding never fires for the input
 * it exists for. Identity falls back to the name, so a collector that supplies
 * no identity behaves exactly as this did before.
 */
function distinctResources(occurrences: readonly StreamOccurrence[]): ResourceRef[] {
  const byKey = new Map<string, ResourceRef>();
  for (const occurrence of occurrences) {
    const name = typeof occurrence.resource === "string" ? occurrence.resource : "";
    const declared =
      typeof occurrence.resourceIdentity === "string" ? occurrence.resourceIdentity : "";
    const identity = declared !== "" ? declared : name;
    const origin = typeof occurrence.resourceOrigin === "string" ? occurrence.resourceOrigin : "";
    const key = normalizeName(identity);
    if (!byKey.has(key)) byKey.set(key, { identity, name, origin });
  }
  return [...byKey.values()].sort(
    (a, b) => compareText(a.name, b.name) || compareText(a.identity, b.identity),
  );
}

/**
 * One label per resource in the whole namespace, keyed the way findings look up.
 *
 * Bounded by the same cap as the analysis pass so that a pathological drop does
 * not pay for the labelling twice; the resources beyond it produce no findings
 * to label, and the caller is told the pass stopped.
 */
function resourceLabelIndex(assets: readonly StreamAsset[]): Map<string, string> {
  const occurrences: StreamOccurrence[] = [];
  for (const asset of assets.slice(0, MAX_ASSETS_SCANNED)) {
    if (!asset) continue;
    for (const occurrence of asset.occurrences ?? []) {
      if (isUsableOccurrence(occurrence)) occurrences.push(occurrence);
    }
  }
  const resources = distinctResources(occurrences);
  const labels = resourceLabels(resources);
  const index = new Map<string, string>();
  resources.forEach((resource, position) => {
    index.set(normalizeName(resource.identity), labels[position]);
  });
  return index;
}

/**
 * The words for each resource, made unambiguous before they reach a sentence.
 *
 * A finding that reads "police and police each ship police.yft" is a finding a
 * user cannot act on: it names no archive they can open, so the report is
 * technically true and practically useless, which is the failure mode this whole
 * layer is written against.
 *
 * This used to append `(from <origin>)` to every resource that had an origin and
 * only then test for ambiguity, which contradicted the sentence it was written
 * under. An origin is per-container, so a picked `resources/` folder gives every
 * pack in it the same one, and a collision between two differently-named packs
 * read "bcso (from resources) and lspd (from resources) each ship police.yft" —
 * a parenthetical identical on both sides of the "and", lengthening the one
 * sentence the user is meant to act on and distinguishing nothing in it.
 *
 * The rule is now `labels.ts`, shared with the headings `report.ts` writes so
 * that the finding and the section a reader scrolls to spell a pack the same
 * way: widen with the origin only for names that repeat, then with the identity
 * only for the ones the origin could not separate. The vendor-zip sentence this
 * whole thread was written for is unchanged — there the names repeat and the
 * origins differ, so step two fires and step three does not.
 */
function resourceLabels(resources: readonly ResourceRef[]): string[] {
  return disambiguateLabels(resources, PROSE_LABEL_STYLE);
}

/**
 * Whether the base game owns this name, or `null` when we could not find out.
 *
 * The index is fetched and decompressed on first use, so it can fail long after
 * the caller handed it over. A failure is not evidence that the name is custom,
 * and the gap between "not stock" and "could not tell" is the gap between a
 * report that is quietly wrong and one that can be trusted; the caller turns the
 * second answer into a line in `skipped`.
 */
function lookUpVanillaName(
  basename: string,
  index: VanillaAssetNameIndex | undefined,
): boolean | null {
  if (!index || typeof index.has !== "function") return null;
  try {
    return index.has(basename) === true;
  } catch {
    return null;
  }
}

/**
 * `a`, `b` and `c` — or `a`, `b`, `c`, and 4 more, once the list stops being a
 * sentence.
 *
 * Takes labels, not names: `resourceLabels` has already decided how to spell
 * each resource so that no two entries in one sentence read alike. Doing that
 * here instead would mean this function needed the whole set to word one entry.
 */
function listResources(labels: readonly string[]): string {
  const shown = labels
    .slice(0, MAX_RESOURCES_NAMED)
    .map((label) => (label === "" ? "an unnamed resource" : label));
  const hidden = labels.length - shown.length;
  if (shown.length === 0) return "Several resources";
  if (hidden > 0) return `${shown.join(", ")}, and ${hidden} more`;
  if (shown.length === 1) return shown[0];
  return `${shown.slice(0, -1).join(", ")} and ${shown[shown.length - 1]}`;
}

function collisionFinding(
  asset: StreamAsset,
  occurrences: readonly StreamOccurrence[],
  labels: readonly string[],
  vanilla: boolean,
): GtaFinding {
  const vanillaNote = vanilla
    ? ` The base game also owns ${asset.basename}, so whichever of these wins is replacing the ` +
      "stock asset for everything in the world that used it, as well as beating the other resource."
    : "";
  return {
    code: "stream.basename.duplicate",
    severity: "error",
    title: `${asset.basename} is streamed by ${labels.length} resources`,
    detail:
      `${listResources(labels)} each ship an asset called ${asset.basename}. The stream ` +
      "namespace is flat — there are no folders in it — so the server loads exactly one of " +
      "them, which one is decided by resource start order, and nothing is written to the " +
      "console when it happens. The pack that loses shows a wrong or invisible model with no " +
      `error to explain it.${vanillaNote} Rename the asset in one of them, or ship it from a ` +
      "single resource.",
    file: occurrences[0].path,
    entity: { kind: "stream_asset", id: asset.basename },
    tags: vanilla ? ["vanilla"] : undefined,
  };
}

/**
 * Named by the same label the collision sentence uses, and not by the bare name.
 *
 * This finding only fires inside *one* resource, which made a bare name look
 * safe. It is not: the report it lands in can hold a second resource with the
 * same name, and "police ships extra.yft at 2 paths" is then equally true of the
 * other police and points the reader at neither. The label widens only when
 * something else in the finding's own occurrence list shares the name, so the
 * ordinary single-pack case still reads as a plain sentence.
 */
function pathDuplicateFinding(
  asset: StreamAsset,
  occurrences: readonly StreamOccurrence[],
  label?: string,
): GtaFinding {
  const resource = label || occurrences[0].resource || "This resource";
  const shownPaths = occurrences.slice(0, MAX_PATHS_NAMED).map((o) => o.path);
  const hiddenPaths = occurrences.length - shownPaths.length;
  const paths =
    hiddenPaths > 0 ? `${shownPaths.join(", ")}, and ${hiddenPaths} more` : shownPaths.join(", ");
  const wasted = occurrences.slice(1).reduce((total, o) => total + sizeOf(o.size), 0);
  const cost =
    wasted > 0
      ? ` The extra copies add ${formatBytes(wasted)} to a resource that will never load them.`
      : "";
  return {
    code: "stream.path.duplicate",
    severity: "warning",
    title: `${resource} ships ${asset.basename} at ${occurrences.length} paths`,
    detail:
      `${paths} all stream under the same name, and the stream namespace is flat, so only one ` +
      "of them ever reaches the game and which one is not defined. This one is contained inside " +
      "a single resource rather than a fight between two, which is why it is a warning and not " +
      `an error.${cost} Delete the copy you do not want, or give it a name of its own.`,
    file: occurrences[1].path,
    entity: { kind: "stream_asset", id: asset.basename },
  };
}

/** Named the same way, for the same reason. See `pathDuplicateFinding`. */
function vanillaOverrideFinding(
  asset: StreamAsset,
  occurrences: readonly StreamOccurrence[],
  label?: string,
): GtaFinding {
  const resource = label || occurrences[0].resource || "this resource";
  return {
    code: "stream.basename.vanilla_override",
    severity: "warning",
    title: `${asset.basename} replaces a base-game asset`,
    detail:
      `${asset.basename} is a name the base game already owns, and ${resource} is the only ` +
      "resource here that ships it, so this is a replacement rather than a conflict and it " +
      "will work exactly as intended: the game loads yours instead of Rockstar's. It is " +
      "listed because a replacement is global — every place in the world that used the stock " +
      "asset changes with it, not only the vehicles this resource spawns. If you meant to add " +
      "a vehicle rather than replace one, give the model a name of its own.",
    file: occurrences[0].path,
    entity: { kind: "stream_asset", id: asset.basename },
    tags: ["vanilla"],
  };
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${Math.round(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Spawn candidates ────────────────────────────────────────────────────────

/**
 * The mod-shop part vocabulary, as the previous generation of this tool used it.
 *
 * These are the words modders put in the file name of a bumper or a spoiler, and
 * a `.yft` whose name contains one is far more likely to be a part of a vehicle
 * than a vehicle. Unanchored and without a trailing boundary on purpose: it is
 * inherited behaviour, `_grill` and `_grille` both have to match, and tightening
 * it would change which names survive on packs people have already scanned.
 */
const MOD_PART_TOKEN =
  /_(spoiler|bumper|skirt|exhaust|bonnet|hood|roof|wing|chassis\d|grille?|mod|livery|door|wheel|fender|diffuser|splitter|canard|trim|cage|interior|enginebay|extra|badge|plate)/i;

/**
 * The other part convention: an abbreviation and a number.
 *
 * `bbr_1.yft`, `chr_2.yft` — the parts of one body kit, numbered in the order
 * the mod shop lists them. They are not spawn names, and the prior art let every
 * one of them through into the spawn list because it only ever looked for words.
 * Two digits at most, so a model year such as `mustang_2015` is left alone.
 */
const NUMBERED_PART_NAME = /_\d{1,2}$/;

export type SpawnCandidate = {
  name: string;
  confidence: "high" | "medium" | "low";
  reason: string;
  source: string;
};

/**
 * Rank the models in a pile of stream files by how likely each is a spawn name.
 *
 * This is a guess and it is presented as one. `vehicles.meta` is the only place
 * a spawn name is actually defined, and a caller holding one should use it. What
 * this is for is the pack that ships thirty `.yft` files and no metadata at all,
 * where the alternative to a ranked guess is nothing.
 *
 * The signal doing the real work is the texture dictionary: a vehicle ships a
 * `.ytd` with its model because it has a paint job, and a bumper usually does not.
 *
 * Where this departs from the tool it replaces, deliberately: the prior art
 * *deleted* every candidate without a matching `.ytd` as soon as any candidate
 * had one, and deleted every name containing a part word, silently and with no
 * way to see what had gone. A real vehicle called `sultan_roof` simply vanished.
 * Here nothing is deleted — a weak candidate comes back at `low` with the
 * sentence explaining why, so the user can overrule it.
 */
export function spawnCandidates(assets: readonly StreamAsset[]): SpawnCandidate[] {
  /** The texture dictionary found for each model stem, under the name it really has. */
  const textures = new Map<string, string>();
  /** One entry per model stem, preferring the base model over its `_hi` companion. */
  const models = new Map<string, { source: string; fromHighLodOnly: boolean }>();

  let scanned = 0;
  for (const asset of assets ?? []) {
    scanned += 1;
    if (scanned > MAX_ASSETS_SCANNED) break;
    if (!asset || typeof asset.basename !== "string") continue;
    const occurrence = (asset.occurrences ?? []).find(isUsableOccurrence);
    if (!occurrence) continue;
    const stem = stemOf(stripHighLod(asset.basename));
    if (stem === "") continue;
    const highLod = isHighLodName(asset.basename);

    if (asset.extension === "ytd") {
      // `police.ytd` beats `police+hi.ytd` as the file the sentence names. A pack
      // can ship the sharpened dictionary and not the base one, and pointing the
      // user at a file their folder does not contain is how they conclude the
      // report is describing somebody else's pack.
      if (!textures.has(stem) || !highLod) textures.set(stem, asset.basename);
      continue;
    }
    if (asset.extension !== "yft") continue;

    const existing = models.get(stem);
    if (existing && (!existing.fromHighLodOnly || highLod)) continue;
    models.set(stem, { source: qualifiedSource(occurrence), fromHighLodOnly: highLod });
  }

  const candidates: SpawnCandidate[] = [];
  for (const [name, model] of models) {
    if (candidates.length >= MAX_SPAWN_CANDIDATES) break;
    candidates.push({
      name,
      ...rankCandidate(name, textures.get(name), model.fromHighLodOnly),
      source: model.source,
    });
  }

  candidates.sort(
    (a, b) =>
      confidenceRank(a.confidence) - confidenceRank(b.confidence) || compareText(a.name, b.name),
  );
  return candidates;
}

/**
 * `police_pack/stream/police.yft` — a bare path repeats in every resource of a pack.
 *
 * Prefixed with the resource's identity rather than its name, because this
 * string's whole job is to be a file the reader can go and open. Where identity
 * is the drop-relative root it prefixes `vendor-a.zip/police/…`, which is the
 * only spelling that points at one file when two packs are both called `police`;
 * where no identity was supplied it falls back to the name and this is the
 * string it always was.
 */
function qualifiedSource(occurrence: StreamOccurrence): string {
  const name = typeof occurrence.resource === "string" ? occurrence.resource : "";
  const declared =
    typeof occurrence.resourceIdentity === "string" ? occurrence.resourceIdentity : "";
  const resource = declared !== "" ? declared : name;
  return resource === "" ? occurrence.path : `${resource}/${occurrence.path}`;
}

function confidenceRank(confidence: SpawnCandidate["confidence"]): number {
  if (confidence === "high") return 0;
  return confidence === "medium" ? 1 : 2;
}

/**
 * Why this name got the rank it got, in a sentence the user can argue with.
 *
 * The order is a claim about how much each signal is worth. A name the engine
 * cannot hash is a fact; a part word is a convention; the presence of a texture
 * dictionary is a correlation. The strongest claim available is the one reported.
 *
 * Note that no branch builds a pattern out of the name, and none may. Texture
 * dictionaries are named `police+hi.ytd`; a `+` handed to `new RegExp` unescaped
 * is a quantifier with nothing in front of it, which throws, and the whole spawn
 * list disappears because one file in the pack was correctly named.
 */
function rankCandidate(
  name: string,
  texture: string | undefined,
  fromHighLodOnly: boolean,
): { confidence: SpawnCandidate["confidence"]; reason: string } {
  const highLodNote = fromHighLodOnly
    ? " Only the high-detail model was found, so the base model is either missing from this " +
      "pack or shipped by another resource."
    : "";

  if (!isValidSpawnName(name)) {
    const bad = invalidSpawnNameChars(name).join(" ");
    return {
      confidence: "low",
      reason:
        `The name contains ${bad}, which the game cannot hash. Spawning it would produce a ` +
        `hash nothing matches and the vehicle would simply not exist.${highLodNote}`,
    };
  }

  const token = MOD_PART_TOKEN.exec(name);
  if (token) {
    return {
      confidence: "low",
      reason:
        `The name contains ${token[0]}, the convention modders use for a mod shop part, so ` +
        "this is more likely a bumper or a spoiler belonging to another vehicle than a " +
        "vehicle of its own. Kept at low confidence rather than hidden, because a real " +
        `vehicle can be named this way.${highLodNote}`,
    };
  }

  if (NUMBERED_PART_NAME.test(name)) {
    return {
      confidence: "low",
      reason:
        "The name ends in a number after an underscore, which is how the parts of one body " +
        "kit are numbered, so it is more likely a part than a vehicle. Kept at low confidence " +
        `rather than hidden, because plenty of real add-ons are numbered too.${highLodNote}`,
    };
  }

  if (texture) {
    return {
      confidence: "high",
      reason:
        `${name}.yft ships alongside ${texture}, which is what a complete vehicle looks ` +
        `like: the model, and the texture dictionary its paint job lives in.${highLodNote}`,
    };
  }

  return {
    confidence: "medium",
    reason:
      `${name}.yft has no matching ${name}.ytd here. That is normal for a vehicle whose ` +
      "textures are shared or shipped by another resource, and it is also what a prop or a " +
      `mod part looks like, so the name is offered without the confirming evidence.${highLodNote}`,
  };
}

// ─── High-detail companions ──────────────────────────────────────────────────

/**
 * Group the high-detail companions under the asset each one belongs to.
 *
 * `police_hi.yft` is not a second vehicle and `police+hi.ytd` is not a second
 * texture dictionary; they are the close-up versions the game swaps in when the
 * camera is near, and a tool that lists them as separate assets doubles the
 * apparent size of every vehicle pack it ever looks at.
 *
 * The grouping is by base asset, not by base stem, so `ambo_hi.yft` is filed
 * under `ambo.yft` and `ambo+hi.ytd` under `ambo.ytd`. That matches
 * `StreamAsset.highLodOf`, and it keeps a missing high-detail model distinct
 * from a missing high-detail texture — two different symptoms in game.
 *
 * The base is reported whether or not it is present in the pile. A companion
 * whose base is missing is worth seeing precisely because it is broken, and a
 * grouping that dropped it would hide that.
 */
export function highLodGroups(
  assets: readonly StreamAsset[],
): { base: string; companions: string[] }[] {
  const groups = new Map<string, Set<string>>();

  let scanned = 0;
  for (const asset of assets ?? []) {
    scanned += 1;
    if (scanned > MAX_ASSETS_SCANNED) break;
    if (!asset || typeof asset.basename !== "string" || asset.basename === "") continue;
    const base = asset.highLodOf ?? baseAssetOf(asset.basename).highLodOf;
    if (!base) continue;
    if (!groups.has(base) && groups.size >= MAX_HIGH_LOD_GROUPS) break;
    const companions = groups.get(base) ?? new Set<string>();
    companions.add(asset.basename);
    groups.set(base, companions);
  }

  return [...groups.entries()]
    .map(([base, companions]) => ({ base, companions: [...companions].sort(compareText) }))
    .sort((a, b) => compareText(a.base, b.base));
}
